import {
  doc,
  getDoc,
  collection,
  addDoc,
  query,
  orderBy,
  getDocs,
  where,
  serverTimestamp
} from 'firebase/firestore';
import { db, uploadImage } from './firebase';
import { handleFirestoreError } from './error-handlers';

export async function getUserData(userId, toast) {
  try {
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (userDoc.exists()) {
      return userDoc.data();
    }
    return null;
  } catch (error) {
    handleFirestoreError(error, toast, 'Failed to fetch user data');
    return null;
  }
}

export async function saveIdentification(userId, identificationData) {
  try {
    // Upload image to Storage and get URL
    const imageUrl = await uploadImage(
      identificationData.imageUrl,
      userId,
      'plant'
    );

    // Save identification with Storage URL and plant name
    const identificationsRef = collection(
      db,
      'users',
      userId,
      'identifications'
    );
    await addDoc(identificationsRef, {
      ...identificationData,
      imageUrl,
      createdAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error saving identification:', error);
    throw error;
  }
}

export async function getUserHistory(userId, toast) {
  try {
    const identificationsRef = collection(
      db,
      'users',
      userId,
      'identifications'
    );
    const q = query(identificationsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    handleFirestoreError(error, toast, 'Failed to fetch history');
    return [];
  }
}

export async function getUserStats(userId) {
  try {
    // Get identifications collection reference
    const identificationsRef = collection(
      db,
      'users',
      userId,
      'identifications'
    );
    const diseaseDetectionsRef = collection(
      db,
      'users',
      userId,
      'diseaseDetections'
    );

    // Get current date and last month's date
    const now = new Date();
    const lastMonth = new Date(
      now.getFullYear(),
      now.getMonth() - 1,
      now.getDate()
    );

    // Get total identifications
    const totalIdentificationsSnap = await getDocs(identificationsRef);
    const totalIdentifications = totalIdentificationsSnap.size;

    // Get total disease detections
    const totalDiseaseDetectionsSnap = await getDocs(diseaseDetectionsRef);
    const totalDiseaseDetections = totalDiseaseDetectionsSnap.size;

    // Calculate active streak
    let activeStreak = 0;
    const dates = totalIdentificationsSnap.docs
      .map(doc => new Date(doc.data().createdAt).toDateString())
      .sort()
      .reverse();

    if (dates.length > 0) {
      activeStreak = 1;
      for (let i = 1; i < dates.length; i++) {
        const current = new Date(dates[i - 1]);
        const prev = new Date(dates[i]);
        const diffDays = Math.floor((current - prev) / (1000 * 60 * 60 * 24));
        if (diffDays === 1) {
          activeStreak++;
        } else {
          break;
        }
      }
    }

    // Get recent activity
    const recentActivityQuery = query(
      identificationsRef,
      orderBy('createdAt', 'desc'),
      where('createdAt', '>=', lastMonth.toISOString())
    );
    const recentActivitySnap = await getDocs(recentActivityQuery);
    const recentActivity = recentActivitySnap.docs.map(doc => {
      const data = doc.data();
      return {
        type: 'identification',
        title: data.plantInfo.split('\n')[0], // First line of plant info
        timestamp: new Date(data.createdAt).toLocaleDateString(),
        imageUrl: data.imageUrl // Include the image URL
      };
    });

    return {
      totalIdentifications,
      totalDiseaseDetections,
      activeStreak,
      recentActivity: recentActivity.slice(0, 5) // Last 5 activities
    };
  } catch (error) {
    console.error('Error fetching user stats:', error);
    return {
      totalIdentifications: 0,
      totalDiseaseDetections: 0,
      activeStreak: 0,
      recentActivity: []
    };
  }
}

// Add this new function
export async function saveDiseaseDetection(userId, detectionData, toast) {
  try {
    // Upload image to Storage and get URL
    const imageUrl = await uploadImage(
      detectionData.imageUrl,
      userId,
      'disease'
    );

    // Save detection with Storage URL
    const detectionsRef = collection(db, 'users', userId, 'diseaseDetections');
    await addDoc(detectionsRef, {
      ...detectionData,
      imageUrl, // Use the Storage URL instead of base64
      createdAt: serverTimestamp()
    });
  } catch (error) {
    handleFirestoreError(error, toast, error.message);
    throw error;
  }
}

// Add this new function
export async function getDiseaseHistory(userId, toast) {
  try {
    const detectionsRef = collection(db, 'users', userId, 'diseaseDetections');
    const q = query(detectionsRef, orderBy('createdAt', 'desc'));
    const snapshot = await getDocs(q);

    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    handleFirestoreError(
      error,
      toast,
      'Failed to fetch disease detection history'
    );
    return [];
  }
}
